/* ============================================================
   CHAOS PDP Sticky Nav — runtime.

   1. Visibility: show the bar once the customer has scrolled past
      the buy box (or the configured pixel offset as a fallback).

   2. Anchor cleanup: remove jump links whose target sections are
      not present on this product (e.g. Loadout on a rod PDP).

   3. Active-anchor highlighting: IntersectionObserver on each
      target section sets .is-active on the matching anchor.

   4. Smooth scroll: anchor clicks scroll with an offset for the
      sticky bar's own height.

   5. Variant sync: the bar's hidden form input and price display
      mirror whatever variant the main buy box has selected. Hooks
      into Shopify's on:variant:change event AND the legacy
      #main_variant_select native change event used by the
      spool-product-options snippet.

   6. ATC: form submit posts to /cart/add.js via fetch so the
      header cart-badge interceptor in nav-hover-panel.js picks up
      the change. Opens cart-drawer if available.
   ============================================================ */
(function () {
  function init() {
    const bar = document.querySelector('[data-cf-pdp-stickynav]');
    if (!bar) return;
    bar.hidden = false;

    const variantData = readVariantData(bar);
    cleanupAnchors(bar);
    wireTopLink(bar);
    wireVisibility(bar);
    wireSmoothScroll(bar);
    wireActiveAnchor(bar);
    wireVariantSync(bar, variantData);
    wireAddToCart(bar);
  }

  function readVariantData(bar) {
    const node = bar.querySelector('[data-cf-stickynav-variants]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (err) {
      return {};
    }
  }

  function cleanupAnchors(bar) {
    const nav = bar.querySelector('.cf-stickynav__anchors');
    if (!nav) return;
    // Remove anchors whose targets aren't on this template (e.g. Loadout
    // anchor on reels/rods PDPs without a chaos-package-loadout section).
    const anchors = Array.from(nav.querySelectorAll('.cf-stickynav__anchor'));
    const live = [];
    anchors.forEach((a) => {
      const id = a.getAttribute('data-cf-stickynav-target');
      const el = id ? document.getElementById(id) : null;
      if (!el) {
        a.remove();
      } else {
        live.push({ anchor: a, top: el.getBoundingClientRect().top + window.scrollY });
      }
    });
    // Re-order anchors to match the DOM order of their target sections.
    // Liquid renders a fixed canonical anchor order (Highlights, Specs,
    // Compare, Loadout, Field Report, FAQ), but on package templates
    // the Loadout section actually sits FIRST — without this resort the
    // sticky-nav order would mismatch the scrolling order, which is
    // confusing.
    live.sort((a, b) => a.top - b.top);
    live.forEach(({ anchor }) => nav.appendChild(anchor));
  }

  function wireTopLink(bar) {
    const link = bar.querySelector('[data-cf-stickynav-top]');
    if (!link) return;
    link.addEventListener('click', (e) => {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  function wireVisibility(bar) {
    // Watch the BOTTOM EDGE of the main-product Shopify section wrapper.
    // The buy box (.product-info) is itself sticky-positioned on this
    // theme, so its bounding rect bounces during scroll and makes for a
    // flaky trigger — when the customer jumps to a section below
    // main-product, the sticky buy-box pops to a position that briefly
    // re-enters the viewport, which was hiding the bar again. The
    // outer section wrapper is static; its bottom edge is permanent.
    // earlyOffset: how many px main-product's bottom edge can still be
    // BELOW the viewport top while we still keep the bar visible. Set
    // generously (default 250) so smooth-scroll clicks to an anchored
    // section don't accidentally pull main-product's bottom back into
    // the trigger zone — the scroll lands such that main-product.bottom
    // ≈ barHeight + a few px, which has to fall comfortably inside this
    // threshold.
    const earlyOffset = parseInt(bar.getAttribute('data-trigger-offset') || '250', 10);
    let mainSection = document.querySelector('[id^="shopify-section"][id$="__main"]');
    if (!mainSection) {
      const buyBox = document.querySelector('.product-info');
      if (buyBox && buyBox.closest) mainSection = buyBox.closest('.shopify-section');
    }

    // Toggling .cf-stickynav-active on <body> drives a CSS rule (in
    // chaos-pdp-sticky-nav.css) that slides the main sticky header off
    // the top. Both bars want top: 0, so we display one at a time
    // instead of stacking.
    const applyVisible = (passed) => {
      bar.classList.toggle('is-visible', passed);
      document.body.classList.toggle('cf-stickynav-active', passed);
    };

    if (mainSection) {
      const check = () => {
        const rect = mainSection.getBoundingClientRect();
        // Show once main-product's bottom edge is within earlyOffset of
        // the viewport top (i.e. main-product is mostly scrolled past).
        // Strict <= 0 was hiding the bar after smooth-scroll clicks.
        applyVisible(rect.bottom <= earlyOffset);
      };
      window.addEventListener('scroll', check, { passive: true });
      window.addEventListener('resize', check);
      check();
      return;
    }

    // Scroll-threshold fallback (used if main-product section wrapper
    // can't be found — shouldn't trigger on real PDPs).
    const onScroll = () => {
      const passed = (window.scrollY || window.pageYOffset || 0) > 400;
      applyVisible(passed);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  function getBarHeight(bar) {
    return bar.getBoundingClientRect().height || 64;
  }

  function wireSmoothScroll(bar) {
    bar.querySelectorAll('.cf-stickynav__anchor').forEach((a) => {
      a.addEventListener('click', (e) => {
        const targetId = a.getAttribute('data-cf-stickynav-target');
        const target = document.getElementById(targetId);
        if (!target) return;
        e.preventDefault();
        const offset = getBarHeight(bar) + 12;
        const y = target.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: y, behavior: 'smooth' });
        // Update hash without re-jumping.
        if (history.replaceState) {
          history.replaceState(null, '', '#' + targetId);
        }
      });
    });
  }

  function wireActiveAnchor(bar) {
    const anchors = Array.from(bar.querySelectorAll('.cf-stickynav__anchor'));
    if (!anchors.length || !('IntersectionObserver' in window)) return;

    const targets = anchors
      .map((a) => {
        const id = a.getAttribute('data-cf-stickynav-target');
        const el = id ? document.getElementById(id) : null;
        return el ? { anchor: a, target: el } : null;
      })
      .filter(Boolean);
    if (!targets.length) return;

    const setActive = (active) => {
      targets.forEach(({ anchor }) => {
        anchor.classList.toggle('is-active', anchor === active);
      });
    };

    // Track the most-visible section: it gets the active state.
    const visibility = new Map();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          visibility.set(entry.target, entry.intersectionRatio);
        });
        let best = null;
        let bestRatio = 0;
        targets.forEach(({ anchor, target }) => {
          const ratio = visibility.get(target) || 0;
          if (ratio > bestRatio) {
            bestRatio = ratio;
            best = anchor;
          }
        });
        if (best) setActive(best);
      },
      { rootMargin: '-25% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );

    targets.forEach(({ target }) => io.observe(target));
  }

  function wireVariantSync(bar, variantData) {
    const input = bar.querySelector('[data-cf-stickynav-variant-input]');
    const priceWrap = bar.querySelector('[data-cf-stickynav-price]');
    const atcBtn = bar.querySelector('[data-cf-stickynav-atc]');
    const atcLabel = bar.querySelector('[data-cf-stickynav-atc-label]');

    const update = (id) => {
      if (!id || !variantData[id]) return;
      const v = variantData[id];
      if (input) input.value = id;
      if (priceWrap) {
        const parts = [];
        if (v.compare_at) {
          parts.push(`<span class="cf-stickynav__price-was">${v.compare_at}</span>`);
          parts.push(`<span class="cf-stickynav__price-now cf-stickynav__price-now--sale">${v.price}</span>`);
        } else {
          parts.push(`<span class="cf-stickynav__price-now">${v.price}</span>`);
        }
        priceWrap.innerHTML = parts.join('');
      }
      if (atcBtn && atcLabel) {
        if (v.available) {
          atcBtn.disabled = false;
          atcLabel.textContent = 'Add to Cart';
        } else {
          atcBtn.disabled = true;
          atcLabel.textContent = 'Sold out';
        }
      }
    };

    // Modern Shopify dispatched event from product-form / variant pickers.
    document.addEventListener('on:variant:change', (e) => {
      const v = e && e.detail && e.detail.variant;
      if (v && v.id) update(v.id);
    });

    // Legacy spool-product-options select — used on reels-template variants.
    const legacySelect = document.getElementById('main_variant_select');
    if (legacySelect) {
      legacySelect.addEventListener('change', (e) => {
        update(e.target.value);
      });
    }

    // Generic fallback: any form input or select named "id" that sits in
    // a product form near the buy box.
    document.querySelectorAll('form[action*="/cart/add"] [name="id"]').forEach((el) => {
      if (el.matches('[data-cf-stickynav-variant-input]')) return;
      el.addEventListener('change', () => {
        update(el.value);
      });
    });
  }

  function wireAddToCart(bar) {
    const form = bar.querySelector('[data-cf-stickynav-form]');
    if (!form) return;
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = form.querySelector('[data-cf-stickynav-atc]');
      const label = form.querySelector('[data-cf-stickynav-atc-label]');
      if (!btn || btn.disabled) return;
      const originalText = label ? label.textContent : '';
      btn.disabled = true;
      if (label) label.textContent = 'Adding…';
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: new FormData(form),
          headers: { Accept: 'application/javascript' },
        });
        if (!res.ok) throw new Error('cart add failed');
        if (label) label.textContent = 'Added';
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') drawer.open();
        setTimeout(() => {
          if (label) label.textContent = originalText || 'Add to Cart';
          btn.disabled = false;
        }, 1500);
      } catch (err) {
        if (label) label.textContent = 'Try again';
        setTimeout(() => {
          if (label) label.textContent = originalText || 'Add to Cart';
          btn.disabled = false;
        }, 1500);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
